// Quick chat bar — single-line composer sitting at the top of the
// Today view. Submit (Enter or click) hands the text to the parent,
// which seeds the chat view and switches tabs.
//
// Mic taps into the same useMic hook the main composer uses — taps
// once to start recording, again to stop; on a finalized transcript
// the text is appended to the draft so the user can edit before
// sending. No attach button here: attachments are a chat-window-only
// affordance.

import { useRef, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { MicListeningOverlay } from "../../components/mic-listening-overlay";
import { useMic } from "../../lib/use-mic";

interface QuickChatProps {
  onSend: (text: string) => void;
  notify: (msg: string) => void;
}

export function QuickChat({ onSend, notify }: QuickChatProps) {
  const [val, setVal] = useState("");

  // Streaming-replacement semantics — same shape as composer.tsx.
  // Partials are the FULL turn transcript (the model may revise),
  // so we anchor on a baseline captured at stream-start and re-render
  // the input as `${baseline} ${turnText}` on each update.
  const streamBaselineRef = useRef<string>("");
  const mic = useMic({
    onStreamStart: () => {
      setVal((prev) => {
        streamBaselineRef.current = prev;
        return prev;
      });
    },
    onStreamFinal: (turnText) => {
      const baseline = streamBaselineRef.current.replace(/\s+$/, "");
      const fullText = baseline ? `${baseline} ${turnText}` : turnText;
      streamBaselineRef.current = "";
      // Voice-assistant auto-send: dictation commits to the model
      // immediately. Empty transcripts (VAD heard nothing) just clear
      // the baseline without sending.
      const trimmed = fullText.trim();
      if (turnText.trim() && trimmed) {
        setVal("");
        onSend(trimmed);
      } else {
        setVal(fullText);
      }
    },
    onError: (msg) => notify(msg),
  });

  const submit = () => {
    const text = val.trim();
    if (!text) return;
    setVal("");
    onSend(text);
  };

  return (
    <div className="today-quickchat">
      {(mic.state === "streaming" || mic.state === "transcribing") && (
        <MicListeningOverlay
          state={mic.state}
          level={mic.level}
          onStop={() => void mic.toggle()}
          onCancel={() => mic.cancel()}
        />
      )}
      <div className="today-quickchat-icon">
        <Icon name="sparkle" size={18} />
      </div>
      <input
        className="today-quickchat-input"
        placeholder="Ask anything…"
        value={val}
        onInput={(e) => setVal((e.currentTarget as HTMLInputElement).value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
      />
      {mic.available && (
        <button
          className="icon-btn today-quickchat-mic"
          data-mic={mic.state}
          style={{ "--mic-level": mic.level } as Record<string, string | number>}
          title={
            mic.state === "streaming" ? "Stop dictation"
            : mic.state === "uploading" ? "Transcribing…"
            : "Dictate"
          }
          disabled={mic.state === "uploading"}
          onClick={() => mic.toggle()}
          type="button"
        >
          <Icon name="mic" size={16} />
        </button>
      )}
      <button className="btn btn-primary today-quickchat-send" onClick={submit} type="button">
        <Icon name="send" size={14} />
        <span className="today-quickchat-label">Send</span>
      </button>
    </div>
  );
}
